import { commonShortFieldLabels, commonShortNouns } from "./common-short-fields";
import { platformAdminExecutionControlPlane } from "./platform-admin-execution-control-plane";
import { platformAdminExecutionEvents } from "./platform-admin-execution-events";
import { platformAdminExecutionRuntimeValues } from "./platform-admin-execution-runtime-values";

export const platformAdminExecution = {
  title: "Execution Fabric",
  subtitle: "Read-only runtime view of the execution platform.",
  directRead: "This page is directly reading execution-fabric primary runtime data and task APIs.",
  indirectRead:
    "This page has converged to direct execution-fabric mode. Sign in with SSO to view execution control plane data.",
  summaryProvider: "Execution provider: {value}",
  summaryImages: "Images: {count}",
  summaryInstances: "Execution instances: {count}",
  summaryDocker: "Debug containers: {count}",
  summaryTasks: "Tasks: {count}",
  summaryWorkers: "Active workers: {count}",
  summaryPendingOldest: "Oldest pending: {count}s",
  summaryLeaseRisk: "Lease risk: {count}",
  summaryControlPlaneState: "Control-plane state: {value}",
  summaryCriticalAlerts: "Critical alerts: {count}",
  summaryWarningAlerts: "Warning alerts: {count}",
  summaryWarningActions: "Warning actions: {count}",
  summarySaturatedActions: "Saturated actions: {count}",
  summaryCapabilityBlockedActions: "Capability-blocked actions: {count}",
  summaryClaimConflicted: "Claim conflicts: {count}",
  summaryRateLimited: "Rate-limited blocks: {count}",
  summaryCapabilityMismatched: "Capability mismatches: {count}",
  summaryDispatchFailed: "Dispatch failed: {count}",
  summaryCoveredWorkerPolicies: "Covered worker policies: {count}",
  summaryMissingWorkerPolicies: "Missing worker policies: {count}",
  summaryAgeWarningActions: "Age-warning actions: {count}",
  summaryRunning: "Running: {count}",
  summaryFailed: "Failed: {count}",
  summaryRetryableFailed: "Retryable failures: {count}",
  summaryAutoRetryScheduled: "Auto-retry pending: {count}",
  legend: {
    provider: "Execution provider",
    images: "Images",
    instances: "Execution instances",
    docker: "Container instances",
    tasks: "Tasks",
    workers: "Active workers",
    pendingOldest: "Oldest pending",
    leaseRisk: "Lease risk",
    controlPlaneState: "Control-plane state",
    criticalAlerts: "Critical alerts",
    warningAlerts: "Warning alerts",
    warningActions: "Warning actions",
    saturatedActions: "Saturated actions",
    capabilityBlockedActions: "Capability-blocked actions",
    claimConflicted: "Claim conflicts",
    rateLimited: "Rate-limited blocks",
    capabilityMismatched: "Capability mismatches",
    dispatchFailed: "Dispatch failed",
    coveredWorkerPolicies: "Covered worker policies",
    missingWorkerPolicies: "Missing worker policies",
    ageWarningActions: "Age-warning actions",
    running: "Running",
    failed: "Failed",
    retryableFailed: "Retryable failures",
    autoRetryScheduled: "Auto-retry pending",
  },
  providerLabel: "Current execution provider: {value}",
  centerBaseUrl: "Execution fabric URL: {value}",
  openCenter: "Open Execution Fabric",
  openExamExecution: "Open Exam Execution Page",
  cards: {
    access: "Access",
    controlPlane: "Control Plane",
  },
  ...platformAdminExecutionControlPlane,
  tabs: {
    images: "Images ({count})",
    instances: "Execution Instances ({count})",
    docker: "Container Instances ({count})",
    tasks: "Execution Tasks ({count})",
    events: "Runtime Events",
    rawEvents: "Raw Events",
    errorCodes: "Error Codes ({count})",
  },
  columns: {
    instance: "Instance ID",
    containerId: "Container ID",
    businessApp: "Service Code / Source App",
    businessObject: "Business Object",
    instanceKind: "Instance Kind",
    runtimeType: "Runtime Type",
    question: "Question",
    status: commonShortNouns.status,
    type: "Type",
    accessMethods: "Access Methods",
    primaryAccessEndpoint: "Primary Access Endpoint",
    accessEndpoint: "Access Endpoint",
    createdAt: commonShortFieldLabels.createdAt,
    expiresAt: "Expires At",
    destroyedAt: "Destroyed At",
    image: "Image",
    hostPort: "Host Port",
    containerPort: "Container Port",
    source: "Source",
    size: "Size",
    defaultPort: "Default Port",
    tags: "Tags",
    container: "Container",
    updatedAt: commonShortFieldLabels.updatedAt,
    keyword: "Keyword",
    retryableOnly: "Retryable Only",
    task: "Task",
    action: "Action",
    operator: "Operator",
    eventConclusion: "Conclusion",
    requestId: "Idempotency Key",
    trace: "Trace",
    retryFrom: "Retry From",
    retryState: "Retry State",
    failureCode: "Failure Code",
    autoRetry: "Auto Retry",
    error: "Error",
    actions: "Actions",
    resourceType: "Resource Type",
    eventStatus: "Event Status",
    eventCategory: "Event Category",
    event: "Event",
    resource: "Resource",
    eventTime: "Event Time",
    code: "Error Code",
    name: commonShortFieldLabels.name,
    category: "Category",
    stage: "Stage",
    retryable: "Retryable",
    description: "Description",
  },
  values: {
    enabled: "Enabled",
    disabled: "Disabled",
    undefinedRuntimeTargetStatus: "Undefined runtime target status",
    yes: "Yes",
    no: "No",
    ...platformAdminExecutionRuntimeValues.values,
    retryable: platformAdminExecutionEvents.values.retryable,
    retried: platformAdminExecutionEvents.values.retried,
    succeeded: platformAdminExecutionEvents.values.succeeded,
    exhausted: platformAdminExecutionEvents.values.exhausted,
    scheduled: platformAdminExecutionEvents.values.scheduled,
    retryScheduled: platformAdminExecutionEvents.values.retryScheduled,
    processing: platformAdminExecutionEvents.values.processing,
    dispatchRetried: platformAdminExecutionEvents.values.dispatchRetried,
    dispatchFailed: platformAdminExecutionEvents.values.dispatchFailed,
    deadLettered: platformAdminExecutionEvents.values.deadLettered,
    manualInterventionRequired: platformAdminExecutionEvents.values.manualInterventionRequired,
    autoRetryExhausted: platformAdminExecutionEvents.values.autoRetryExhausted,
    pending: platformAdminExecutionEvents.values.pending,
    running: platformAdminExecutionEvents.values.running,
    success: platformAdminExecutionEvents.values.success,
    failed: platformAdminExecutionEvents.values.failed,
    cancelled: platformAdminExecutionEvents.values.cancelled,
    eventTaskPending: platformAdminExecutionEvents.values.eventTaskPending,
    eventTaskClaimed: platformAdminExecutionEvents.values.eventTaskClaimed,
    eventTaskClaimReleased: platformAdminExecutionEvents.values.eventTaskClaimReleased,
    eventTaskRunning: platformAdminExecutionEvents.values.eventTaskRunning,
    eventTaskSucceeded: platformAdminExecutionEvents.values.eventTaskSucceeded,
    eventTaskFailed: platformAdminExecutionEvents.values.eventTaskFailed,
    eventTaskCancelled: platformAdminExecutionEvents.values.eventTaskCancelled,
    eventTaskCancellationRequested:
      platformAdminExecutionEvents.values.eventTaskCancellationRequested,
    eventTaskLeaseRenewed: platformAdminExecutionEvents.values.eventTaskLeaseRenewed,
    eventTaskClaimConflicted: platformAdminExecutionEvents.values.eventTaskClaimConflicted,
    eventTaskDispatchCapabilityMismatch:
      platformAdminExecutionEvents.values.eventTaskDispatchCapabilityMismatch,
    eventTaskDispatchRateLimited: platformAdminExecutionEvents.values.eventTaskDispatchRateLimited,
    eventTaskAutoRetryDispatchFailed:
      platformAdminExecutionEvents.values.eventTaskAutoRetryDispatchFailed,
    eventTaskAutoRetryDeadLettered:
      platformAdminExecutionEvents.values.eventTaskAutoRetryDeadLettered,
    eventTaskAutoRetryManualInterventionRequired:
      platformAdminExecutionEvents.values.eventTaskAutoRetryManualInterventionRequired,
    eventTaskStaleRunningDetected:
      platformAdminExecutionEvents.values.eventTaskStaleRunningDetected,
    eventStatusRecorded: platformAdminExecutionEvents.values.eventStatusRecorded,
    eventCategoryTask: platformAdminExecutionEvents.values.eventCategoryTask,
    eventCategoryDispatch: platformAdminExecutionEvents.values.eventCategoryDispatch,
    eventCategoryLease: platformAdminExecutionEvents.values.eventCategoryLease,
    eventCategoryRecovery: platformAdminExecutionEvents.values.eventCategoryRecovery,
    eventCategoryOther: platformAdminExecutionEvents.values.eventCategoryOther,
    resourceExecutionTask: platformAdminExecutionEvents.values.resourceExecutionTask,
    resourceEnvironmentInstance: platformAdminExecutionEvents.values.resourceEnvironmentInstance,
    resourceExecutionTaskWithId: platformAdminExecutionEvents.values.resourceExecutionTaskWithId,
    resourceEnvironmentInstanceWithId:
      platformAdminExecutionEvents.values.resourceEnvironmentInstanceWithId,
    eventConclusionSuccess: platformAdminExecutionEvents.values.eventConclusionSuccess,
    eventConclusionFailed: platformAdminExecutionEvents.values.eventConclusionFailed,
    eventConclusionFailedWithReason:
      platformAdminExecutionEvents.values.eventConclusionFailedWithReason,
    eventConclusionRetryState: platformAdminExecutionEvents.values.eventConclusionRetryState,
  },
  legendSemantic: {
    all: "All",
    total: "Total",
    online: "Online",
    offline: "Offline",
    unknown: "Unknown",
    enabled: "Enabled",
    disabled: "Disabled",
    running: "Running",
    failed: "Failed",
    destroyed: "Destroyed",
    warning: "Warning",
    draft: "Draft",
    healthy: "Healthy",
    default: "Default",
    active: "Active",
    current: "Current",
    retry: "Retry",
    remaining: "Remaining",
    pending: "Processing",
    succeeded: "Succeeded",
    published: "Published",
    archived: "Archived",
    public: "Public",
    private: "Private",
    shared: "Shared",
    mine: "Mine",
    used: "Used",
    unused: "Unused",
    visible: "Visible",
    hidden: "Hidden",
    inactive: "Inactive",
    degraded: "Degraded",
    mvp: "Basic",
    reserved: "Reserved",
    container: "Container",
    vm: "VM",
    expired: "Expired",
    console: "Console",
    recycles: "Recycle Tasks",
    operator: "Operator",
    create: "Create",
    update: "Update",
    delete: "Delete",
    template: "Template",
  },
  actions: {
    view: "View",
    retry: platformAdminExecutionEvents.actions.retry,
    retryTask: platformAdminExecutionEvents.actions.retryTask,
    processAutoRetries: platformAdminExecutionEvents.actions.processAutoRetries,
  },
  drawer: {
    title: platformAdminExecutionEvents.drawer.title,
    titleShort: platformAdminExecutionEvents.drawer.titleShort,
    eventTitle: platformAdminExecutionEvents.drawer.eventTitle,
    eventTitleShort: platformAdminExecutionEvents.drawer.eventTitleShort,
    instanceTitle: "Instance #{id}",
    dockerInstanceTitle: "Runtime Container · {id}",
    rawEventTitle: platformAdminExecutionEvents.drawer.rawEventTitle,
    rawEventTitleShort: platformAdminExecutionEvents.drawer.rawEventTitleShort,
    requestPayload: platformAdminExecutionEvents.drawer.requestPayload,
    resultPayload: platformAdminExecutionEvents.drawer.resultPayload,
  },
  fields: {
    appName: "Business Display Name",
    appKey: "Service Code",
    businessObject: "Business Object",
    instanceKind: "Instance Kind",
    runtimeType: "Runtime Type",
    providerType: "Runtime Provider",
    providerKey: "Provider Key",
    nodeKey: "Runtime Node",
    targetKey: "Runtime Target",
    providerRef: "Runtime Reference",
    taskId: platformAdminExecutionEvents.fields.taskId,
    tenantId: "Tenant ID",
    organizationId: "Organization ID",
    examId: "Exam ID",
    questionId: "Question ID",
    attemptId: "Attempt ID",
    attemptQuestionId: "Attempt Question ID",
    candidateId: "Candidate ID",
    sourceType: "Source Type",
    objectType: "Object Type",
    questionTitle: "Question Title",
    displayOrder: "Display Order",
    questionType: "Business Question Type",
    environmentType: "Environment Type",
    eventAction: platformAdminExecutionEvents.fields.eventAction,
    eventTrigger: platformAdminExecutionEvents.fields.eventTrigger,
    workerId: platformAdminExecutionEvents.fields.workerId,
    requiredCapability: platformAdminExecutionEvents.fields.requiredCapability,
    dispatchDecision: platformAdminExecutionEvents.fields.dispatchDecision,
    leaseOwner: platformAdminExecutionEvents.fields.leaseOwner,
    leaseExpiresAt: platformAdminExecutionEvents.fields.leaseExpiresAt,
    retrySource: platformAdminExecutionEvents.fields.retrySource,
    rootTask: platformAdminExecutionEvents.fields.rootTask,
    retryDepth: platformAdminExecutionEvents.fields.retryDepth,
    retryCount: platformAdminExecutionEvents.fields.retryCount,
    autoRetryEnabled: platformAdminExecutionEvents.fields.autoRetryEnabled,
    autoRetryState: platformAdminExecutionEvents.fields.autoRetryState,
    autoRetryMax: platformAdminExecutionEvents.fields.autoRetryMax,
    autoRetryDelay: platformAdminExecutionEvents.fields.autoRetryDelay,
    nextRetryAt: platformAdminExecutionEvents.fields.nextRetryAt,
    latestRetry: platformAdminExecutionEvents.fields.latestRetry,
    startTime: platformAdminExecutionEvents.fields.startTime,
    finishTime: platformAdminExecutionEvents.fields.finishTime,
  },
  contract: {
    missingServiceIdentity: "Missing source_app/app_key",
  },
  columnHelp: {
    event: platformAdminExecutionEvents.columnHelp.event,
    resource: platformAdminExecutionEvents.columnHelp.resource,
    status: platformAdminExecutionEvents.columnHelp.status,
    eventConclusion: platformAdminExecutionEvents.columnHelp.eventConclusion,
    eventCategory: platformAdminExecutionEvents.columnHelp.eventCategory,
  },
  messages: {
    taskDetailLoadFailed: platformAdminExecutionEvents.messages.taskDetailLoadFailed,
    taskRetryStarted: platformAdminExecutionEvents.messages.taskRetryStarted,
    taskRetryFailed: platformAdminExecutionEvents.messages.taskRetryFailed,
    autoRetryProcessed: platformAdminExecutionEvents.messages.autoRetryProcessed,
    autoRetryProcessFailed: platformAdminExecutionEvents.messages.autoRetryProcessFailed,
    errorCodesEmpty:
      "The error-code catalog is empty. This usually means execution-fabric reading failed, direct-read state is abnormal, or page data did not load correctly.",
  },
  units: {
    seconds: "{value} s",
    workersShort: "workers",
  },
} as const;
